import {
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useNotificationSummary,
} from '@/api/hooks'
import type { NotificationView } from '@/api/types'
import {
  EmptyState,
  ErrorBanner,
  formatDateTime,
  humanise,
  isoDate,
  MetricCard,
  PageHeader,
  StatusChip,
} from '@/components/common'

const CHANNELS = ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP', 'PUSH']
const STATUSES = ['QUEUED', 'SENT', 'FAILED']

/**
 * The documents a message can be about, and where each one lives.
 *
 * <p>The reference the notification carries is the table it came from; this is
 * how it reads to the office and where clicking it goes. Anything not listed
 * still shows, under its own name - a new kind of message must appear in the
 * inbox rather than vanish because nobody added it here.</p>
 */
const DOCUMENTS: Record<string, { label: string; path?: (id: string) => string }> = {
  enquiry: { label: 'Enquiry', path: (id) => `/enquiries/${id}` },
  quotation: { label: 'Quotation', path: (id) => `/quotations/${id}` },
  order: { label: 'Order', path: (id) => `/orders/${id}` },
  payment: { label: 'Receipt', path: () => '/receipts' },
  payment_schedule: { label: 'Payment due' },
  kitchen_request: { label: 'Kitchen request', path: () => '/kitchen' },
  manpower_requirement: { label: 'Staffing', path: () => '/staff' },
  vehicle_requirement: { label: 'Logistics', path: () => '/logistics' },
  feedback_request: { label: 'Feedback', path: () => '/feedback' },
  service_recovery_case: { label: 'Service recovery', path: () => '/feedback' },
  OTHER: { label: 'Other' },
}

const documentLabel = (value?: string) =>
  value ? DOCUMENTS[value]?.label ?? humanise(value) : 'Other'

/**
 * The inbox: every message this person has been sent, with the counts that say
 * what is waiting and filters to read one kind of document at a time.
 */
export function NotificationsPage() {
  const navigate = useNavigate()
  const [documentType, setDocumentType] = useState('')
  const [channel, setChannel] = useState('')
  const [status, setStatus] = useState('')
  const [unreadOnly, setUnreadOnly] = useState('')
  // Opens on today. Every list and every report starts on the day being
  // worked, and the range is widened when somebody wants history.
  const [from, setFrom] = useState(() => isoDate(0))
  const [to, setTo] = useState(() => isoDate(0))
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)

  const range = { from, to }
  const { data: summary } = useNotificationSummary(range)
  const { data, isLoading, error } = useNotifications({
    documentType: documentType || undefined,
    channel: channel || undefined,
    status: status || undefined,
    unreadOnly: unreadOnly === 'unread' ? true : undefined,
    ...range,
    page,
    size,
  })
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()

  const open = (notification: NotificationView) => {
    if (!notification.readAt) markRead.mutate(notification.id)
    const document = notification.referenceType ? DOCUMENTS[notification.referenceType] : undefined
    if (document?.path && notification.referenceId) {
      navigate(document.path(notification.referenceId))
    }
  }

  const narrow = (next: string) => {
    setDocumentType(next)
    setPage(0)
  }

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle="Everything the system has sent you, by document"
        actions={
          <Button
            startIcon={<DoneAllIcon />}
            disabled={(summary?.unread ?? 0) === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate(documentType || undefined)}
          >
            {documentType ? `Mark ${documentLabel(documentType).toLowerCase()} read` : 'Mark all read'}
          </Button>
        }
      />
      <ErrorBanner error={error ?? markAllRead.error} />

      {/* ------------------------------------------------------------ counts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <MetricCard label="In your inbox" value={summary?.total ?? 0} caption="messages" />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Unread"
            value={summary?.unread ?? 0}
            caption={summary?.unread ? 'waiting on you' : 'nothing waiting'}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Not delivered"
            value={summary?.failed ?? 0}
            caption={summary?.failed ? 'worth chasing' : 'all delivered'}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Kinds of document"
            value={summary?.byDocument.length ?? 0}
            caption="in this window"
          />
        </Grid>
      </Grid>

      {/* One chip per document actually in the inbox: the count is the filter. */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip
          label={`All ${summary?.total ?? 0}`}
          color={documentType === '' ? 'primary' : 'default'}
          variant={documentType === '' ? 'filled' : 'outlined'}
          onClick={() => narrow('')}
        />
        {(summary?.byDocument ?? []).map((entry) => (
          <Chip
            key={entry.documentType}
            label={`${documentLabel(entry.documentType)} ${entry.total}${
              entry.unread ? ` · ${entry.unread} unread` : ''
            }`}
            color={documentType === entry.documentType ? 'primary' : 'default'}
            variant={documentType === entry.documentType ? 'filled' : 'outlined'}
            onClick={() => narrow(entry.documentType)}
          />
        ))}
      </Stack>

      {/* ----------------------------------------------------------- filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Document"
              value={documentType}
              onChange={(event) => narrow(event.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All documents</MenuItem>
              {(summary?.byDocument ?? []).map((entry) => (
                <MenuItem key={entry.documentType} value={entry.documentType}>
                  {documentLabel(entry.documentType)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Channel"
              value={channel}
              onChange={(event) => {
                setChannel(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All channels</MenuItem>
              {CHANNELS.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Delivery"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Any</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Read"
              value={unreadOnly}
              onChange={(event) => {
                setUnreadOnly(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">Read and unread</MenuItem>
              <MenuItem value="unread">Unread only</MenuItem>
            </TextField>
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value)
                setPage(0)
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value)
                setPage(0)
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------ inbox */}
      <Card>
        {isLoading && <LinearProgress />}
        {data && data.content.length === 0 ? (
          <EmptyState message="Nothing here. Widen the dates or clear the filters." />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Document</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Delivery</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.content.map((notification) => (
                  <TableRow
                    key={notification.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => open(notification)}
                  >
                    <TableCell>
                      {/* Unread reads bolder, so the eye finds it without a column for it. */}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: notification.readAt ? 400 : 700 }}
                      >
                        {formatDateTime(notification.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={documentLabel(notification.referenceType)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{humanise(notification.channel)}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: notification.readAt ? 400 : 700 }}>
                      {notification.subject ?? '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>{notification.body ?? '—'}</TableCell>
                    <TableCell>
                      <StatusChip status={notification.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data && (
              <TablePagination
                component="div"
                count={data.totalElements}
                page={data.page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(event) => {
                  setSize(Number(event.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            )}
          </>
        )}
      </Card>
    </>
  )
}
